import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/types';
import { Enable2faResponse } from '../types';

export const profileAPI = createApi({
  reducerPath: 'profileAPI',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    enable2FA: builder.mutation<ApiResponse<Enable2faResponse>, void>({
      query: (body) => ({
        url: 'api/auth/enable-2fa/',
        method: 'POST',
        body,
      }),
    }),
    verifyEnable2FA: builder.mutation<
      ApiResponse<any>,
      { code: string | number }
    >({
      query: (body) => ({
        url: 'api/auth/verify-enable-2fa/',
        method: 'POST',
        body,
      }),
    }),
    disable2FA: builder.mutation<
      ApiResponse<{ message: string }>,
      { code: string | number }
    >({
      query: (body) => ({
        url: 'api/auth/disable-2fa/',
        method: 'POST',
        body,
      }),
    }),
    deleteUser: builder.mutation<ApiResponse<{ message: string }>, void>({
      query: () => ({
        url: 'api/user/delete-user/',
        method: 'DELETE',
      }),
    }),
    sendOTP: builder.mutation<ApiResponse<any>, void>({
      query: (body) => ({
        url: 'api/auth/send-otp/',
        method: 'POST',
        body,
      }),
    }),
    getTFNNumber: builder.mutation<
      ApiResponse<{ tfn_number: string }>,
      { code: string }
    >({
      query: (body) => {
        return {
          url: 'api/user/verify-otp-and-view-tfn/',
          method: 'POST',
          body,
        };
      },
    }),
    updateTFNNumber: builder.mutation<
      ApiResponse<{ message: string }>,
      { tfn_number: string }
    >({
      query: (body) => {
        return {
          url: 'api/user/tfn-update/otp/send/',
          method: 'POST',
          body,
        };
      },
    }),
    verifyUpdateTFNNumber: builder.mutation<
      ApiResponse<{ message: string; tfn_number: string }>,
      { code: string }
    >({
      query: (body) => {
        return {
          url: 'api/user/tfn-update/otp/verify/',
          method: 'POST',
          body,
        };
      },
    }),
    generateImpersonationToken: builder.mutation<ApiResponse<any>, any>({
      query: (body) => ({
        url: 'api/user/generate-impersonation-token/',
        method: 'POST',
        body,
      }),
    }),
    getHouseholdExpenditure: builder.query<ApiResponse<any>, void>({
      query: () => ({
        url: 'api/hem/',
        method: 'GET',
      }),
    }),
    patchHouseholdExpenditure: builder.mutation<ApiResponse<any>, any>({
      query: (body) => ({
        url: 'api/hem/',
        method: 'PATCH',
        body,
      }),
    }),
    postFeedback: builder.mutation<ApiResponse<any>, any>({
      query: (body) => ({
        url: '/api/core/feedback/create/',
        method: 'POST',
        body,
      }),
    }),
    // TFN send OTP and verify OTP
    sendTFNOTP: builder.mutation<ApiResponse<any>, void>({
      query: () => ({
        url: 'api/user/tfn/view/send/otp/',
        method: 'POST',
      }),
    }),
    verifyTFNOTP: builder.mutation<ApiResponse<any>, { code: string }>({
      query: (body) => ({
        url: 'api/user/tfn/view/verify/otp/',
        method: 'POST',
        body,
      }),
    }),
    //TFN update OTP
    sendTFNUpdateOTP: builder.mutation<
      ApiResponse<any>,
      { tfn_number: string }
    >({
      query: (body) => ({
        url: 'api/user/tfn/update/send/otp/',
        method: 'POST',
        body,
      }),
    }),
    verifyTFNUpdateOTP: builder.mutation<ApiResponse<any>, { code: string }>({
      query: (body) => ({
        url: 'api/user/tfn/update/verify/otp/',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useEnable2FAMutation,
  useVerifyEnable2FAMutation,
  useDisable2FAMutation,
  useDeleteUserMutation,
  useSendOTPMutation,
  useGetTFNNumberMutation,
  useUpdateTFNNumberMutation,
  useVerifyUpdateTFNNumberMutation,
  useGenerateImpersonationTokenMutation,
  usePatchHouseholdExpenditureMutation,
  useGetHouseholdExpenditureQuery,
  usePostFeedbackMutation,
  useSendTFNOTPMutation,
  useVerifyTFNOTPMutation,
  useSendTFNUpdateOTPMutation,
  useVerifyTFNUpdateOTPMutation,
} = profileAPI;
